import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { memberApi, getMemberToken, setMemberToken, errMsg } from "../lib/api";
import Sidebar, { MobileNav } from "./Sidebar";
import ChatView from "./ChatView";
import RightRail from "./RightRail";
import { FromMeView, ForYouView, ChooseView, UpdatesView } from "./ContentViews";
import { ProfileView, SubscriptionView, SettingsView, HelpView, InactiveView } from "./AccountViews";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import "./members.css";

export const SECTIONS = ["chat", "from-me", "for-you", "choose", "updates", "profile", "subscription", "settings", "help"];

export default function MemberApp() {
  const { section = "chat" } = useParams();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => { setMemberToken(null); nav("/"); }, [nav]);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await memberApi.get("/member/me");
      setUser(data);
      return data;
    } catch (e) {
      setMemberToken(null);
      nav("/?login=1");
      return null;
    }
  }, [nav]);

  const loadHome = useCallback(async () => {
    try { const { data } = await memberApi.get("/member/home"); setHome(data); }
    catch (e) { if (e?.response?.status !== 402) toast.error(errMsg(e, "Could not load your space")); }
  }, []);

  useEffect(() => {
    if (!getMemberToken()) { nav("/?login=1"); return; }
    (async () => { const u = await loadMe(); if (u?.active) await loadHome(); setLoading(false); })();
  }, []); // eslint-disable-line

  useEffect(() => { if (!SECTIONS.includes(section)) nav("/members/chat", { replace: true }); }, [section, nav]);

  const go = (s) => nav(`/members/${s}`);

  if (loading || !user) {
    return <div className="mem-root min-h-screen grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#e8365f]" /></div>;
  }

  const accountSections = ["profile", "subscription", "settings", "help"];
  const locked = !user.active && !accountSections.includes(section);

  const views = {
    chat: <ChatView user={user} onUnreadChange={loadHome} />,
    "from-me": <FromMeView />,
    "for-you": <ForYouView user={user} />,
    choose: <ChooseView onChange={loadHome} />,
    updates: <UpdatesView />,
    profile: <ProfileView user={user} setUser={setUser} />,
    subscription: <SubscriptionView user={user} setUser={setUser} reload={async () => { const u = await loadMe(); if (u?.active) loadHome(); }} />,
    settings: <SettingsView user={user} setUser={setUser} />,
    help: <HelpView />,
  };

  return (
    <div className="mem-root min-h-screen lg:p-3">
      <div className="mem-shell">
        <Sidebar active={section} go={go} onLogout={logout} />
        <div className="flex-1 min-w-0 flex flex-col">
          {/* mobile header */}
          <div className="lg:hidden flex items-center justify-between px-5 pt-4 pb-3 border-b border-[#f1dfe0] bg-[#fdf5f3]">
            <button onClick={() => go("chat")} className="text-left" data-testid="mem-mobile-logo">
              <p className="mem-brand text-[24px]">Silky Maya <span className="text-[#e8365f]">♥</span></p>
              <p className="text-[12.5px] text-[#5a4c4f] -mt-0.5">Our private space</p>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger className="p-2" data-testid="mem-mobile-menu"><MoreVertical className="w-5 h-5" /></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {[["choose", "Choose for Me"], ["updates", "My Updates"], ["subscription", "Manage Subscription"], ["settings", "Settings"], ["help", "Help & Support"]].map(([k, l]) => (
                  <DropdownMenuItem key={k} onClick={() => go(k)}>{l}</DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={logout} className="text-[#e8365f]">Log Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex-1 min-h-0 flex mem-content">
            <main className={`flex-1 min-w-0 ${section === "chat" && !locked ? "" : "overflow-y-auto mem-scroll"}`} data-testid={`mem-section-${section}`}>
              {locked ? <InactiveView user={user} go={go} /> : views[section]}
            </main>
            {section === "chat" && !locked && <RightRail home={home} go={go} onVoted={loadHome} />}
          </div>
          <MobileNav active={section} go={go} />
        </div>
      </div>
    </div>
  );
}
